/**
 * Language code -> ENDONYM: the language's own name (e.g. `mx` "Mexicano", `bra` "Brasileiro",
 * `cfr` "Français canadien"). A language picker lists every option in its OWN tongue, the same
 * regardless of the active UI language, so consumers render this value RAW (never through
 * `t()`). Static, NOT `Intl.DisplayNames`: this deployment ships custom market codes
 * (`co`, `mx`, `bra`, …) that are not standard ISO-639, so `Intl.DisplayNames('co')` would
 * mislabel them ("Corsican").
 *
 * Kept in `config/` with NO store/hook imports, so presentational pickers (and their Playwright
 * component tests) can use it without pulling the Redux-coupled `useLanguageSwitcher` into scope.
 */
export const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  cen: 'Canadian English',
  es: 'Español',
  ro: 'Română',
  pt: 'Português',
  mx: 'Mexicano',
  cl: 'Chileno',
  co: 'Colombiano',
  pe: 'Peruano',
  ve: 'Venezolano',
  arg: 'Argentino',
  bra: 'Brasileiro',
  za: 'Afrikaans',
  cfr: 'Français canadien',
};

/** Display name (endonym) for a language code: the curated map, else Intl, else the code. */
export const languageName = (code: string): string => {
  if (LANGUAGE_NAMES[code]) return LANGUAGE_NAMES[code];
  try {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code);
    if (name) return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    /* Intl.DisplayNames unsupported — fall back to the bare code. */
  }
  return code.toUpperCase();
};
